import { params } from '@app/configurations';
import { RouterPath } from '@app/core/store/states/router-nav/router-nav-state.model';
import {
  customerHasMerchantSpringFeature,
  customersHasGoalsV2,
  customersHasOldGoals,
} from '@app/core/utilities/customer.utilities';
import { CustomerSummary } from './common.models';
import { Customer } from './customer.models';
import { UserRole } from './user.models';

export enum AdspertApps {
  Apollo = 'APOLLO',
  Dashboard = 'DASHBOARD',
  Explorer = 'EXPLORER',
}

export type ExplorerPaths = 'admin/changelog' | 'admin/scopes' | `admin/scopes/account/${number}` | string;
export type ExplorerHash = { customerId?: number; adspertAccountId?: number; scope_type?: 'CUSTOMER' | 'ACCOUNT' };

export class MainMenuLink {
  name?: string;
  key: string;
  path?: string;
  url?: string;
  queryParams?: any;
  permission: UserRole;
  hasSeparator?: boolean;
  app?: AdspertApps;
  isBeta?: boolean;
  isExternal?: boolean;
  params?: any[];
  children?: MainMenuLink[];
  hasDestinationFilters?: boolean;
  hasFeature?: (customer: Customer, summary?: CustomerSummary) => boolean;
  newFeatureValues?: MainMenuLink | {} | undefined;
}

const DefaultParams = [
  {
    key: params.CUSTOMER_ID,
    mandatory: false,
  },
  {
    key: params.ADSPERT_ID,
    mandatory: false,
  },
  {
    key: params.GLOBAL_FILTER_ACCOUNT_QUERY,
    mandatory: false,
  },
  {
    key: params.GLOBAL_FILTER_DATE_RANGE,
    mandatory: true,
  },
  {
    key: params.GLOBAL_FILTER_DATE_COMPARE,
    mandatory: true,
  },
  {
    key: params.GLOBAL_FILTER_PERFORMANCE_GROUP_ID,
    mandatory: false,
  },
  {
    key: params.GLOBAL_FILTER_PERFORMANCE_GROUP_QUERY,
    mandatory: false,
  },
  {
    key: params.GLOBAL_FILTER_CAMPAIGN_ID,
    mandatory: false,
  },
  {
    key: params.GLOBAL_FILTER_CAMPAIGN_QUERY,
    mandatory: false,
  },
  {
    key: params.GLOBAL_FILTER_AD_GROUPS_ID,
    mandatory: false,
  },
  {
    key: params.GLOBAL_FILTER_AD_GROUPS_QUERY,
    mandatory: false,
  },
  {
    key: params.GLOBAL_FILTER_CRITERIA_ID,
    mandatory: false,
  },
  {
    key: params.GLOBAL_FILTER_CRITERIA_QUERY,
    mandatory: false,
  },
  {
    key: params.GLOBAL_FILTER_SEARCH_QUERY,
    mandatory: false,
  },
  {
    key: params.IMPERSONATION_ID,
    mandatory: false,
  },
];

const DestFilterParams = [
  { key: params.GLOBAL_FILTER_DEST_PERFORMANCE_GROUP_QUERY, mandatory: false },
  { key: params.GLOBAL_FILTER_DEST_PERFORMANCE_GROUP_ID, mandatory: false },
  { key: params.GLOBAL_FILTER_DEST_CAMPAIGN_ID, mandatory: false },
  { key: params.GLOBAL_FILTER_DEST_CAMPAIGN_QUERY, mandatory: false },
  { key: params.GLOBAL_FILTER_DEST_AD_GROUPS_ID, mandatory: false },
  { key: params.GLOBAL_FILTER_DEST_AD_GROUPS_QUERY, mandatory: false },
];

export const globalNavigationLinks: MainMenuLink[] = [
  {
    permission: 'USER',
    app: AdspertApps.Apollo,
    params: [...DefaultParams, { key: params.DELAY, mandatory: false }],
    key: 'monitor',
    path: 'monitor',
  },
  {
    permission: 'USER',
    app: AdspertApps.Apollo,
    params: [],
    key: 'control',
    path: '',
    children: [
      {
        permission: 'USER',
        app: AdspertApps.Apollo,
        key: 'goals',
        path: 'goals',
        params: DefaultParams,
        hasFeature: customersHasOldGoals,
      },
      {
        permission: 'USER',
        app: AdspertApps.Apollo,
        key: 'pg-goals',
        path: 'pg-goals',
        params: DefaultParams,
        hasFeature: customersHasGoalsV2,
      },
      {
        permission: 'USER',
        app: AdspertApps.Apollo,
        key: 'optimization-settings',
        path: RouterPath.OptimizationSettings,
        params: DefaultParams,
      },
    ],
  },
  {
    permission: 'USER',
    params: DefaultParams,
    app: AdspertApps.Apollo,
    key: 'creation',
    path: 'campaign',
  },
  {
    permission: 'USER',
    path: '',
    params: [],
    key: 'criterion-addition',
    app: AdspertApps.Apollo,
    children: [
      {
        permission: 'USER',
        app: AdspertApps.Apollo,
        params: [...DefaultParams],
        key: 'criterion-addition-rules',
        path: 'criterion-hub/rules',
      },
      {
        permission: 'USER',
        app: AdspertApps.Apollo,
        params: [...DefaultParams],
        hasDestinationFilters: true,
        key: 'criterion-addition-suggestions-review',
        path: 'criterion-hub/suggestions-review',
      },
      {
        permission: 'USER',
        app: AdspertApps.Apollo,
        params: [...DefaultParams, ...DestFilterParams],
        hasDestinationFilters: true,
        key: 'criterion-addition-product-ads-sync',
        path: 'criterion-hub/product-ads-sync',
      },
    ],
  },
  {
    permission: 'USER',
    app: AdspertApps.Apollo,
    params: [],
    key: 'reports',
    path: '',
    children: [
      {
        permission: 'USER',
        app: AdspertApps.Apollo,
        params: [
          ...DefaultParams,
          {
            key: params.KEY_FIGURE_TAB,
            mandatory: false,
          },
        ],
        key: 'key-figures',
        path: 'keyfigures',
      },
      {
        permission: 'USER',
        key: 'retail-analytics',
        isExternal: true,
        url: 'https://mm.merchantspring.io/',
        hasFeature: customerHasMerchantSpringFeature,
      },
      {
        permission: 'USER',
        app: AdspertApps.Apollo,
        params: DefaultParams,
        key: 'logs',
        path: 'log',
      },
      {
        permission: 'USER',
        key: 'budget-suggestions',
        app: AdspertApps.Apollo,
        params: [...DefaultParams, { key: params.CUSTOMER_ID, mandatory: true }],
        path: 'budget-suggestions',
      },
      {
        permission: 'USER',
        app: AdspertApps.Apollo,
        params: [
          ...DefaultParams,
          { key: params.CUSTOMER_ID, mandatory: true },
          { key: params.ADSPERT_ID, mandatory: true },
        ],
        key: 'scenarios',
        path: 'scenarios',
        hasFeature: customersHasOldGoals,
      },
    ],
  },
  {
    permission: 'USER',
    params: DefaultParams,
    app: AdspertApps.Apollo,
    key: 'products',
    path: 'products',
  },
  {
    permission: 'SYS-ADMIN',
    app: AdspertApps.Apollo,
    params: [],
    key: 'admin',
    path: '',
    children: [
      {
        permission: 'SYS-ADMIN',
        app: AdspertApps.Apollo,
        params: DefaultParams,
        key: 'reporting',
        path: 'admin/reporting',
      },
      {
        permission: 'SYS-ADMIN',
        app: AdspertApps.Explorer,
        params: [
          { key: params.CUSTOMER_ID, mandatory: false },
          { key: params.ADSPERT_ID, mandatory: false },
        ],
        key: 'customers',
        path: 'admin/scopes',
        hasSeparator: false,
      },
      {
        permission: 'SYS-ADMIN',
        app: AdspertApps.Explorer,
        params: [
          { key: params.CUSTOMER_ID, mandatory: false },
          { key: params.ADSPERT_ID, mandatory: false },
        ],
        key: 'accounts',
        path: 'admin/scopes',
        hasSeparator: false,
      },
      {
        permission: 'SYS-ADMIN',
        app: AdspertApps.Explorer,
        params: [
          { key: params.CUSTOMER_ID, mandatory: false },
          { key: params.ADSPERT_ID, mandatory: false },
        ],
        key: 'job_queue',
        path: 'jobs/queue',
        hasSeparator: false,
      },
      {
        permission: 'SYS-ADMIN',
        app: AdspertApps.Explorer,
        params: [
          { key: params.CUSTOMER_ID, mandatory: false },
          { key: params.ADSPERT_ID, mandatory: false },
        ],
        key: 'job_history',
        path: 'jobs/history',
        hasSeparator: false,
      },
      {
        permission: 'SYS-ADMIN',
        app: AdspertApps.Explorer,
        params: [],
        key: 'deployment_log',
        path: 'admin/deployments',
        hasSeparator: false,
      },
      {
        permission: 'SYS-ADMIN',
        app: AdspertApps.Explorer,
        params: [{ key: params.CUSTOMER_ID, mandatory: false }],
        key: 'admin_log',
        path: 'admin/changelog',
        hasSeparator: false,
      },
      {
        permission: 'SYS-ADMIN',
        app: AdspertApps.Explorer,
        params: [{ key: params.CUSTOMER_ID, mandatory: false }],
        key: 'users',
        path: 'admin/users',
        hasSeparator: false,
      },
      {
        permission: 'SYS-ADMIN',
        app: AdspertApps.Explorer,
        params: [],
        key: 'detective',
        path: 'detective/query',
        hasSeparator: false,
      },
      {
        permission: 'SYS-ADMIN',
        app: AdspertApps.Explorer,
        params: [...DefaultParams, { key: params.DELAY, mandatory: false }],
        key: 'forecast_quality',
        path: 'forecast-quality-apollo',
        hasSeparator: false,
      },
    ],
  },
];
